import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewEncapsulation, Input } from '@angular/core';
import { Subscription, Subject, Observable, forkJoin, of, filter} from 'rxjs';
import { map, switchMap, debounceTime, catchError } from 'rxjs/operators';

import { MessageService} from 'primeng/api';

import { environment } from 'src/environments/environment';

import { Product, ProductInfo } from 'src/app/shared/product'

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { RestApiService } from 'src/app/services/rest-api.service';
import { ProductApiService } from 'src/app/services/product-api.service';

/** One price line of the panel: the product's prices expressed in a single currency. */
export interface ProductPriceRow {
  /** Currency the four prices below are expressed in. */
  currId: number;
  /** `CURCODE` of that currency (trimmed); `#<id>` when the lookup failed. */
  code: string;
  /** Why the row is shown: main, balance and/or price-calculation currency (one row can carry several). */
  roles: string[];
  retail: number;
  base: number;
  qty: number;
  partner: number;
}

/**
 * Read-only panel that displays stock, pricing and discount information for a single product.
 *
 * The prices are shown as one row per currency, each fetched from the API in that currency
 * (never derived by multiplying another row by a rate, which gave a different figure from the
 * price-calculation table whenever the product is priced in a rate-variant currency such as
 * `EUR_RETAIL`):
 * - main currency (`mainCurrencyId`);
 * - balance currency (`balanceCurrencyId`, the `currencyId` the pipeline runs with);
 * - the price-calculation currency, i.e. the currency of the product's default supplier
 *   (`VENDORCURRID`) - only when it differs from the two above, otherwise its role is added to
 *   the row that already shows that currency.
 *
 * The main pipeline listens on `listOptions$` (a `{ PRODID, CURRID }` Subject).  When both fields
 * are non-null, it debounces (300 ms) then:
 * 1. `getProdInfoById` - stock, discounts and the prices in the balance currency (`CURRID`);
 * 2. in parallel, for every other currency of the list above: `getProdPriceInfoById` (prices only,
 *    no stock) and `getCurrencyInfoByID` (the currency code used as the row label).
 *
 * Three input paths push onto `listOptions$`: `currencyId`, `prodId` and `product` (its `PRODID`).
 * On init, `currencyId` is seeded from `GetglobalsettingsService.balanceCurrencyId`.
 */
@Component({
  selector: 'app-product-info',
  templateUrl: './product-info.component.html',
  styleUrls: ['./product-info.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ProductInfoComponent implements OnInit, OnDestroy, OnChanges {

  globalSettinsData: GlobalSettingsData;

  private _prodInfoQuery: Subscription;

  /*private*/ _prodId: number;

  /** Internal Subject carrying `{ PRODID, CURRID }` pairs to the pricing pipeline. */
  private listOptions$ = new Subject < { PRODID: number, CURRID: number } >();

  /** Raw rows returned by `getProdInfoById` (balance currency); the stock figures come from here. */
  prodInfo: ProductInfo[];

  /** Price rows, one per currency, in display order (main, balance, price-calculation). */
  priceRows: ProductPriceRow[];

  /** Base discount percentage from `PRODBASEDISC`. */
  basediscount: number = 0;
  /** Quantity discount percentage from `PRODQTYDISC`. */
  qntydiscount: number = 0;
  /** Partner (dialog) discount percentage from `PRODDIALDISC`. */
  partnerdiscount: number = 0;

  /** The balance currency ID; seeded from global settings on init. */
  @Input() currencyId: number;

  /** The product ID whose pricing info should be displayed. */
  @Input() prodId: number;

  /** Alternative input: the full `Product` object; `PRODID` is extracted and used as `prodId`. */
  @Input() product: Product;

  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private restapi: RestApiService
  ) {
  }

  /** Reacts to `currencyId`, `prodId`, and `product` changes by pushing onto `listOptions$`. */
  ngOnChanges(changes: SimpleChanges) {
//    console.log('*** ProductInfoComponent ngOnChanges: enter');
    //environment.production || console.table(changes);
    for (let propName in changes) {
      let change = changes[propName];

      environment.production || console.log('*** ProductInfoComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
//      console.log('*** ProductInfoComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        
        case 'currencyId':
        case 'prodId':
          //environment.production || console.log('*** ProductInfoComponent ngOnChanges: prodId ' + this.prodId);

          this.listOptions$.next({ 'PRODID': this.prodId, 'CURRID': this.currencyId });
          break;

        case 'product':
          //environment.production || console.table(change.currentValue);

          let prod: Product = change.currentValue;
          if (prod) {
            this.prodId = prod.PRODID;

            this.listOptions$.next({ 'PRODID': this.prodId, 'CURRID': this.currencyId });
          }
          break;
      }
    }
  }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** ProductInfoComponent in ngOnDestroy');

    if (this._prodInfoQuery) { this._prodInfoQuery.unsubscribe(); }
  }

  /**
   * Seeds `currencyId` from global settings, then sets up the `listOptions$` → `concat` pipeline
   * and triggers the initial load.
   */
  ngOnInit(): void {
    console.log('*** ProductInfoComponent in ngOnInit (enter)');
  /*
   * Start quering product stock info at first and independently
   */

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();
    this.currencyId = this.globalSettinsData.balanceCurrencyId;

    this._prodInfoQuery =
      this.listOptions$.pipe(
        // tap(e => console.log('*** ProductInfoComponent in listOptions$ filter ' + JSON.stringify(e))),
        filter(e => ((e.PRODID != null) && (e.CURRID != null))),
        debounceTime(300),
        // distinctUntilChanged(),

        switchMap(info => {

          console.log('*** ProductInfoComponent in ngOnInit: (' + JSON.stringify(info) + ')');
          return this.prodApi.getProdInfoById(
            info.PRODID,
            this.globalSettinsData.mainCompanyId,
            info.CURRID,
            this.globalSettinsData.mainStockId,
            this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
            this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
            new Date()
          ).pipe(switchMap(data => this.buildPriceRows(info, data)));
        })
    )
      .subscribe();

    this.listOptions$.next({ 'PRODID': this.prodId, 'CURRID': this.currencyId });
  }

  /**
   * Resolves the balance-currency response into `priceRows`: the main currency, the balance
   * currency and the product's price-calculation currency (`VENDORCURRID` of the default
   * supplier), each once - a currency that plays several roles is one row carrying all of them.
   * The balance row comes straight from `data`; the others are fetched in their own currency.
   * A failing extra row is left out rather than blanking the whole panel.
   *
   * Nothing is published until every row has arrived: stock, discount headers and price rows are
   * assigned together at the end, so the table never shows the previous response's prices under
   * the new discounts or stock (a refresh after a save, or a reused panel switching product).
   */
  private buildPriceRows(info: { PRODID: number, CURRID: number }, data: ProductInfo[]): Observable<void> {
    const balance = data[0];
    if (!balance) {
      this.prodInfo = data;
      this.priceRows = [];
      return of(undefined);
    }

    const targets: { currId: number, roles: string[] }[] = [];
    const addTarget = (currId: number, role: string) => {
      if (!currId) { return; }
      const target = targets.find(t => t.currId === currId);
      if (target) { target.roles.push(role); } else { targets.push({ currId, roles: [role] }); }
    };
    addTarget(this.globalSettinsData.mainCurrencyId, 'main');
    addTarget(info.CURRID, 'balance');
    addTarget(balance.VENDORCURRID, 'price calc');

    const rowObservables = targets.map(target => forkJoin({
      price: target.currId === info.CURRID
        ? of(balance)
        : this.prodApi.getProdPriceInfoById(
            info.PRODID,
            this.globalSettinsData.mainCompanyId,
            target.currId,
            this.globalSettinsData.mainStockId,
            this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
            this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
            new Date()
          ).pipe(map(rows => rows[0]), catchError(() => of(undefined))),
      code: this.restapi.getCurrencyInfoByID(target.currId, new Date())
        .pipe(map(currencies => (currencies[0]?.CURCODE || '').trim()), catchError(() => of('')))
    }).pipe(map(({ price, code }): ProductPriceRow | null => price ? {
      currId: target.currId,
      code: code || ('#' + target.currId),
      roles: target.roles,
      retail: price.PRODPRICE,
      base: price.BASEDISCPRICE,
      qty: price.QTYDISCPRICE,
      partner: price.DIALDISCPRICE
    } : null)));

    // forkJoin([]) completes without emitting, which would leave the panel on "loading"
    return (rowObservables.length > 0 ? forkJoin(rowObservables) : of([] as (ProductPriceRow | null)[])).pipe(map(rows => {
      this.prodInfo = data;
      this.basediscount = balance.PRODBASEDISC;
      this.qntydiscount = balance.PRODQTYDISC;
      this.partnerdiscount = balance.PRODDIALDISC;
      this.priceRows = rows.filter((row): row is ProductPriceRow => row !== null);

      console.log('*** ProductInfoComponent onInitTableLoads records: ' + JSON.stringify(this.priceRows));
    }));
  }
}
